import { useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import Modal from "../ui/Modal";
import ConfirmDialog from "../ui/ConfirmDialog";
import FormField, { FormActions, fieldInputStyle } from "../ui/FormField";
import {
  createCliente,
  updateCliente,
  deleteCliente,
} from "../../services/clientesService";
import ClienteDocumentosSection from "./ClienteDocumentosSection";

const ORIGEM_OPTIONS = ["Instagram", "Facebook", "Google", "Indicação", "Outro"];

const EMPTY = {
  nome: "",
  email: "",
  telefone: "",
  passaporte: "",
  pais: "",
  origem: "",
  observacoes: "",
};

// Modal de criação/edição de cliente.
// Se `cliente` vier preenchido → modo edição (PUT + botão Excluir).
// Se `cliente` for null/undefined → modo criação (POST).
export default function ClienteFormModal({ open, cliente, onClose, onSaved, onDeleted }) {
  const { C } = useTheme();
  const isEdit = !!cliente;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Popula o form sempre que o modal abre com um cliente diferente.
  useEffect(() => {
    if (!open) return;
    if (cliente) {
      setForm({
        nome: cliente.nome ?? "",
        email: cliente.email ?? "",
        telefone: cliente.telefone ?? "",
        passaporte: cliente.passaporte ?? "",
        pais: cliente.pais ?? "",
        origem: cliente.origem ?? "",
        observacoes: cliente.observacoes ?? "",
      });
    } else {
      setForm(EMPTY);
    }
    setError(null);
  }, [open, cliente]);

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function close() {
    if (saving || deleting) return;
    onClose?.();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nome.trim()) {
      setError("O nome é obrigatório.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        nome: form.nome.trim(),
        email: form.email.trim() || null,
        telefone: form.telefone.trim() || null,
        passaporte: form.passaporte.trim() || null,
        pais: form.pais.trim() || null,
        origem: form.origem || null,
        observacoes: form.observacoes.trim() || null,
      };
      if (isEdit) {
        await updateCliente(cliente.id, payload);
      } else {
        await createCliente(payload);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.join?.(" ") ||
          "Erro ao salvar cliente."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteCliente(cliente.id);
      setConfirmDelete(false);
      onDeleted?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Erro ao excluir cliente."
      );
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  const inputStyle = fieldInputStyle(C);

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? "Editar Cliente" : "Novo Cliente"}
        subtitle={isEdit ? cliente?.nome : "Cadastre um novo turista"}
        width={560}
      >
        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            <FormField label="Nome" required span={2}>
              <input
                autoFocus
                value={form.nome}
                onChange={(e) => update("nome", e.target.value)}
                style={inputStyle}
                placeholder="Nome completo"
              />
            </FormField>

            <FormField label="E-mail">
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                style={inputStyle}
                placeholder="cliente@email.com"
              />
            </FormField>

            <FormField label="Telefone">
              <input
                value={form.telefone}
                onChange={(e) => update("telefone", e.target.value)}
                style={inputStyle}
                placeholder="+55 11 99999-9999"
              />
            </FormField>

            <FormField label="Passaporte">
              <input
                value={form.passaporte}
                onChange={(e) => update("passaporte", e.target.value)}
                style={inputStyle}
                placeholder="BR1234567"
              />
            </FormField>

            <FormField label="País">
              <input
                value={form.pais}
                onChange={(e) => update("pais", e.target.value)}
                style={inputStyle}
                placeholder="Brasil"
              />
            </FormField>

            <FormField label="Origem" span={2}>
              <select
                value={form.origem}
                onChange={(e) => update("origem", e.target.value)}
                style={inputStyle}
              >
                <option value="">Selecione...</option>
                {ORIGEM_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Observações" span={2}>
              <textarea
                value={form.observacoes}
                onChange={(e) => update("observacoes", e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }}
                placeholder="Preferências, restrições alimentares, etc."
              />
            </FormField>
          </div>

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: C.redDim,
                color: C.red,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}

          <FormActions
            onCancel={close}
            onDelete={isEdit ? () => setConfirmDelete(true) : undefined}
            saving={saving}
            submitLabel={isEdit ? "Salvar alterações" : "Criar cliente"}
          />
        </form>

        {isEdit && cliente?.id && (
          <ClienteDocumentosSection clienteId={cliente.id} />
        )}
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Excluir cliente?"
        message={`Tem certeza que deseja excluir "${cliente?.nome}"? Essa ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        danger
        loading={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}
